import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Flame } from "lucide-react";

const STAGES = ["G_PREPARATION", "G_LINE", "KILN", "POLISHING"];
const STAGE_COLORS = {
  PENDING: "bg-slate-100 text-slate-700",
  IN_PROGRESS: "bg-amber-100 text-amber-900",
  COMPLETE: "bg-emerald-100 text-emerald-900",
  BLOCKED: "bg-red-100 text-red-900",
};

/**
 * KilnLines focuses on shop-floor stage cells only — operator view (vs. ProductionRuns which is manager view).
 */
export default function KilnLines() {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/manufacturing/production-runs").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="kiln-lines-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><Flame className="w-7 h-7 text-rose-600" /> Kiln & shop-floor lines</h1>
        <p className="text-muted-foreground">Live operator view — G-Preparation · G-Line · Kiln · Polishing only. Use Production runs for full lifecycle.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {STAGES.map((stage) => {
          const inThisStage = orders.filter((o) => (o.stage_status?.[stage] || "PENDING") === "IN_PROGRESS");
          const blocked = orders.filter((o) => (o.stage_status?.[stage] || "") === "BLOCKED");
          return (
            <div key={stage} className="surface-card p-5" data-testid={`kiln-stage-${stage}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-black text-lg">{stage.replace(/_/g, " ")}</h3>
                <Badge variant="outline" className="font-mono text-[10px]">{inThisStage.length} active · {blocked.length} blocked</Badge>
              </div>
              <Table className="mt-3">
                <TableHeader><TableRow><TableHead>PO #</TableHead><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No production orders.</TableCell></TableRow>
                  ) : null}
                  {orders.map((o) => {
                    const ss = (o.stage_status || {})[stage] || "PENDING";
                    return (
                      <TableRow key={o.id}>
                        <TableCell className="font-mono text-xs">{o.number}</TableCell>
                        <TableCell className="text-sm">{o.product_name}</TableCell>
                        <TableCell className="font-mono">{o.quantity}</TableCell>
                        <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${STAGE_COLORS[ss] || ""}`}>{ss.replace(/_/g, " ")}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
